import React from "react";

import { MailCheck, MailX } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "~/components/ui/tooltip";
import { useUpdateSessionReviewStatus } from "~/hooks/mutations/use-update-session-review-status";

interface PractitionerReviewButtonProps {
    sessionId: string;
    needsPractitionerReview: boolean;
    isSessionPractitioner: boolean;
    isOwnSession: boolean;
    /** Custom trigger element. When provided, the component clones it with onClick to fire the mutation. */
    trigger?: React.ReactElement;
}

export const PractitionerReviewButton = ({
    sessionId,
    needsPractitionerReview,
    isSessionPractitioner,
    isOwnSession,
    trigger,
}: PractitionerReviewButtonProps) => {
    const { mutate: updateSessionReviewStatus } =
        useUpdateSessionReviewStatus();

    let reviewTooltipMessage = "";

    if (!isSessionPractitioner) {
        reviewTooltipMessage =
            "Only the assigned practitioner can change the review status.";
    } else if (isOwnSession) {
        reviewTooltipMessage = "You cannot review your own session.";
    } else if (needsPractitionerReview) {
        reviewTooltipMessage = "Mark as reviewed and release to your client.";
    } else {
        reviewTooltipMessage =
            "Mark as needs review and withhold from your client.";
    }

    // AIDEV-NOTE: trigger prop allows parent to provide a custom element (e.g. DropdownMenuItem)
    // that fires the review status mutation when selected
    if (trigger) {
        const isDisabled = !isSessionPractitioner || isOwnSession;
        return React.cloneElement(trigger, {
            onSelect: () =>
                updateSessionReviewStatus({
                    sessionId: sessionId,
                    needsReview: !needsPractitionerReview,
                }),
            disabled: isDisabled,
        });
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Button
                    variant="ghost"
                    size="default"
                    // AIDEV-NOTE: "Release"/"Withhold", not "Share"/"Unshare" — this button
                    // clears the review hold; it is not the share dialog next to it, which
                    // practitioners confused (Michelle Glass, 2026-09-05).
                    // hardcoded to make release / withhold the same width in tables
                    style={{ width: "120px" }}
                    disabled={!isSessionPractitioner || isOwnSession}
                    onClick={() =>
                        updateSessionReviewStatus({
                            sessionId: sessionId,
                            needsReview: !needsPractitionerReview,
                        })
                    }
                >
                    {needsPractitionerReview ? (
                        <div className="flex gap-2">
                            <MailCheck className="size-4 mt-0.5" />{" "}
                            <span className="text-sm">Release</span>
                        </div>
                    ) : (
                        <div className="flex gap-2">
                            <MailX className="size-4 mt-0.5" />{" "}
                            <span className="text-sm">Withhold</span>
                        </div>
                    )}
                </Button>
            </TooltipTrigger>
            <TooltipContent>{reviewTooltipMessage}</TooltipContent>
        </Tooltip>
    );
};
